import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircleOutline,
  CheckCircleRounded,
  VerifiedRounded,
  LocalHospitalRounded,
  ShieldRounded,
  DirectionsCarFilledRounded,
} from '@mui/icons-material'
import Logo from '../../../assets/images/ambunetLogo.png'

import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import '../../../components/Fonts.css'
import '../../../app.css'
import { Button } from '@mui/material'
const featureItems = [
  {
    icon: <CheckCircleOutline fontSize="small" />,
    text: 'Check the web address for any spelling mistakes',
  },
  {
    icon: <CheckCircleOutline fontSize="small" />,
    text: 'Make sure you are using the correct company subdomain',
  },
  {
    icon: <CheckCircleOutline fontSize="small" />,
    text: 'Contact your organisation if you believe this is an error',
  },
]

const TenantNotFound = () => {
  const theme = {
    palette: {
      primary: {
        main: '#242F40',
        light: '#4A5568', // lighter version of primary
        dark: '#1A2230', // darker version
        contrastText: '#FFFFFF',
      },

      secondary: {
        main: '#3A475C',
        light: '#5C6A80',
        dark: '#2A3445',
        contrastText: '#FFFFFF',
      },

      third: {
        main: '#239AA4',
        dark: '#1E7F88',
        light: '#4DBBC4',
        contrastText: '#FFFFFF',
      },
    },
  }

  const [showSuccessPage, setShowSuccessPage] = useState(false)

  return (
    <Box
      sx={{
        // minHeight: '100vh',
        bgcolor: '#F7F9FC',
        background: `linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)`,
        p: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{ height: '100%' }}
      >
        <Box
          sx={{
            position: 'relative',
            // height: '100%',
            borderRadius: 5,
            overflow: 'hidden',
            px: { xs: 3, md: 5 },
            py: { xs: 4, md: 5 },
            display: 'flex',
            alignItems: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'white',
            boxShadow: '0 20px 60px rgba(36,47,64,0.16)',
          }}
        >
          <Box
            component="img"
            src={Logo}
            alt="AmbuNet Logo Background"
            sx={{
              position: 'absolute',
              right: { xs: -40, md: -20 },
              top: '50%',
              transform: 'translateY(-50%)',
              width: { xs: '90%', md: '78%' },
              maxWidth: 560,
              opacity: 0.06,
              zIndex: 0,
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          />

          <Stack
            spacing={3}
            sx={{
              position: 'relative',
              zIndex: 1,
              maxWidth: 620,
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                flexWrap="wrap"
              >
                <Box
                  component="img"
                  src={Logo}
                  alt="AmbuNet Logo"
                  sx={{
                    width: { xs: 52, md: 64 },
                    height: 'auto',
                    filter: 'drop-shadow(0 6px 14px rgba(0,0,0,0.12))',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}
                  fontFamily="moonlander"
                >
                  AmbuNet
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontSize: { xs: '2.2rem', md: '2.6rem' },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  maxWidth: 580,
                }}
              >
                This AmbuNet workspace could not be found
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.75,
                  color: 'rgba(255,255,255,0.84)',
                  maxWidth: 560,
                }}
              >
                The address you’ve entered doesn’t match an active AmbuNet
                workspace. This could be due to a typo, an expired account, or
                an incorrect link.
              </Typography>
            </Stack>

            <Stack spacing={1.4}>
              {featureItems.map((item) => (
                <Stack
                  key={item.text}
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.14)',
                      color: theme.palette.third.light,
                      flexShrink: 0,
                      mt: '2px',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.92)',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.98rem', md: '1rem' },
                    }}
                  >
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Paper
              elevation={0}
              sx={{
                mt: 1,
                p: 2.25,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.10)',
                border: '1px solid rgba(255,255,255,0.14)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Stack spacing={1}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#FFFFFF',
                  }}
                >
                  New to AmbuNet?
                </Typography>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.82)',
                    lineHeight: 1.65,
                    fontSize: '0.95rem',
                  }}
                >
                  You can create your own workspace in minutes and start
                  managing your service, staff, and operations in one connected
                  platform.
                </Typography>
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  alignItems="center"
                  //   justifyContent="center"
                  spacing={2}
                >
                  <Button
                    variant="contained"
                    sx={{ mt: 1.5, alignSelf: 'flex-start' }}
                    onClick={() =>
                      (window.location.href = 'https://signup.ambunet.co.uk')
                    }
                  >
                    Create an account
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ mt: 1.5, alignSelf: 'flex-start' }}
                    onClick={() =>
                      (window.location.href = 'https://ambunet.co.uk')
                    }
                  >
                    Visit AmbuNet
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </motion.div>
    </Box>
  )
}

export default TenantNotFound
